import { useEffect, useRef, useMemo } from "react";
import styles from "./shopcategories.module.css";
import { IoFilterSharp } from "react-icons/io5";
import { useLocation, useNavigate } from "react-router-dom";
import { useInfiniteProductsQuery } from "../../../queries/useProductsQuery";
import { Banner, BreadCrumb } from "../../shared/index";
import { ShopProducts, ShopFilters, SortFilter } from "../index";
import useFilters from "../../../hooks/useFilters";
import useInfiniteScroll from "../../../hooks/useInfiniteScroll";
import { useGetWishLists } from "../../../queries/useWishListQuery";
import ProductPopup from "../PopupProduct/PopupProduct";

const formatLabel = (slug = "") =>
  slug.replace(/[_-]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const ShopCategories = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const searchQuery = searchParams.get("search");  
  const categoryFromUrl = searchParams.get("category");
  const subCategoryFromUrl = searchParams.get("subcategory");
  const childCategoryFromUrl = searchParams.get("childcategory");
  const prevCategoryRef = useRef(categoryFromUrl); 
  const {
  data: wishItems = [],
  isPending: isWishListPending,
} = useGetWishLists();
  /**
   * URL IS SOURCE OF TRUTH
   */
  const { filtersProps, filtersActions, queryParams } = useFilters({
    initialSearch: searchQuery,
    initialCategory: categoryFromUrl,
    initialSubCategory: subCategoryFromUrl,
  });
  const { sortBy } = filtersProps;
  const { setSortBy, setMobileFiltersOpen } = filtersActions;
  /**
   * FORCE API PARAMS TO FOLLOW URL
   */
const finalQueryParams = useMemo(() => {
const params = {
...queryParams,
};

// Category filter
if (categoryFromUrl) {
params.category = categoryFromUrl;
} else {
delete params.category;
}

// Subcategory filter
if (subCategoryFromUrl) {
params.subcategory = subCategoryFromUrl;
} else {
delete params.subcategory;
}

// Child category filter — IMPORTANT
// Child category filter
if (childCategoryFromUrl) {
  params.child_category = childCategoryFromUrl;
} else {
  delete params.child_category;
}

// Remove the old parameter if it exists
delete params.childcategory;

// Preserve selected colors
const urlParams = new URLSearchParams(location.search);
const selectedColors = urlParams.getAll("color[]");

if (selectedColors.length > 0) {
params["color[]"] = selectedColors;
} else {
delete params["color[]"];
}

return params;
}, [
queryParams,
categoryFromUrl,
subCategoryFromUrl,
childCategoryFromUrl,
location.search,
]);
// DEBUG: URL filters and API query params
useEffect(() => {
  console.group("🔍 CHILD CATEGORY FILTER DEBUG");

  console.log("Full URL:", window.location.href);
  console.log("location.search:", location.search);

  console.log("categoryFromUrl:", categoryFromUrl);
  console.log("subCategoryFromUrl:", subCategoryFromUrl);
  console.log("childCategoryFromUrl:", childCategoryFromUrl);

  console.log("queryParams from useFilters:", queryParams);
  console.log("finalQueryParams sent to API:", finalQueryParams);

  console.log(
    "Final query string:",
    new URLSearchParams(
      Object.entries(finalQueryParams).flatMap(([key, value]) =>
        Array.isArray(value)
          ? value.map((item) => [key, String(item)])
          : value != null
          ? [[key, String(value)]]
          : []
      )
    ).toString()
  );

  console.groupEnd();
}, [
  location.search,
  categoryFromUrl,
  subCategoryFromUrl,
  childCategoryFromUrl,
  queryParams,
  finalQueryParams,
]);
  /**
   * API CALL
   */
 const {
  data,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  isPending,
  isError,
  refetch,
} = useInfiniteProductsQuery(finalQueryParams, {
  keepPreviousData: true,
  enabled: !!queryParams, // ✅ ADD THIS LINE
});
  
const brands = useMemo(() => {
  return data?.pages?.[0]?.filter_info?.brands || [];
}, [data]);
const colors = useMemo(() => {
  const allColors =
    data?.pages?.flatMap((page) => page?.products?.data ?? [])
      ?.flatMap((product) => product?.variants ?? [])
      ?.filter((variant) => variant?.status !== false)
      ?.map((variant) => ({
        color_name: variant?.color_name,
        color_code: variant?.color_code,
      }))
      ?.filter((color) => color?.color_name || color?.color_code) ?? [];

  return [
    ...new Map(
      allColors.map((color) => [
        color.color_code || color.color_name,
        color,
      ])
    ).values(),
  ];
}, [data]);
  /**      
   * PRODUCTS (NO FRONTEND FILTERING)
   */


const products = useMemo(() => {
  const allProducts =
    data?.pages?.flatMap((page) => page?.products?.data ?? []) ?? [];
 console.log("===== RAW PRODUCTS FROM API =====");

  console.table(
    allProducts.map((product) => ({
      id: product.id,
      name: product.name,
      categorySlug: product.category?.slug,
      subcategorySlug: product.sub_category?.slug,
      childCategorySlug: product.child_category?.slug,
      childCategoryId: product.child_category_id,
      totalVariantQty: product.variants?.reduce(
        (total, variant) =>
          total + Number(variant?.variant_qty || 0),
        0
      ),
    }))
  );
  console.log("===== RAW PRODUCTS FROM API =====");
  console.log(
    allProducts.map((product) => ({
      id: product.id,
      name: product.name,
      category_id: product.category_id,
      sub_category_id: product.sub_category_id,
      child_category_id: product.child_category_id,
      category_slug: product.category?.slug,
      subcategory_slug: product.sub_category?.slug,
      childcategory_slug: product.child_category?.slug,
    }))
  );

  return allProducts
    .map((product) => {
      const activeVariant = Array.isArray(product?.variants)
        ? product.variants.find((variant) => variant?.status !== false)
        : null;

      const totalQty = Array.isArray(product?.variants)
        ? product.variants.reduce(
            (total, variant) =>
              total + Number(variant?.variant_qty || 0),
            0
          )
        : 0;

      return {
        ...product,
        qty: totalQty,
        price: Number(product?.price || 0),
        offer_price: Number(product?.offer_price || 0),
        sku: activeVariant?.variant_sku || product?.sku || "",
      };
    })
    .filter((product) => product.qty > 0)
    .filter((product) => {
      if (
        categoryFromUrl &&
        product.category?.slug !== categoryFromUrl
      ) {
        return false;
      }

      if (
        subCategoryFromUrl &&
        product.sub_category?.slug !== subCategoryFromUrl
      ) {
        return false;
      }

      if (
        childCategoryFromUrl &&
        product.child_category?.slug !== childCategoryFromUrl
      ) {
        return false;
      }

      return true;
    });
}, [
  data,
  categoryFromUrl,
  subCategoryFromUrl,
  childCategoryFromUrl,
]);





useEffect(() => {
  console.group("🛍️ FINAL PRODUCTS SENT TO ShopProducts");

  console.log("Selected child category:", childCategoryFromUrl);
  console.log("Products displayed count:", products.length);

  console.table(
    products.map((product) => ({
      id: product.id,
      name: product.name,
      category: product.category?.slug,
      subcategory: product.sub_category?.slug,
      childcategory: product.child_category?.slug,
      child_category_id: product.child_category_id,
      qty: product.qty,
    }))
  );

  console.groupEnd();
}, [products, childCategoryFromUrl]);
const categoriesRef = useRef([]);

const categories = useMemo(() => {
  const newCategories = data?.pages?.[0]?.filter_info?.categories;

  // ✅ If API returns valid categories → update ref
  if (newCategories && newCategories.length > 0) {
    categoriesRef.current = newCategories;
  }

  // ✅ Always return last valid categories
  return categoriesRef.current;
}, [data]);

const currentCategoryDescription = useMemo(() => {
  if (!categoryFromUrl || !categories.length) return "";

  const currentCategory = categories.find(
    (cat) => cat.slug === categoryFromUrl 
  );

  return currentCategory?.description || "";
}, [categories, categoryFromUrl]); 

const currentSubCategoryDescription = useMemo(() => {
  if (!subCategoryFromUrl || !categories.length) return "";

  for (const cat of categories) {  
    const sub = cat.subcategories?.find(  
      (s) => s.slug === subCategoryFromUrl
    );
    if (sub) return sub.description || "";
  }

  return "";
}, [categories, subCategoryFromUrl]);
  /**
   * 🔥 AUTO-FIX WRONG CATEGORY FROM SUBCATEGORY
   */
  useEffect(() => {
    if (!subCategoryFromUrl || !categories.length) return;
    const parentCategory = categories.find((cat) =>
      cat.subcategories?.some((sub) => sub.slug === subCategoryFromUrl)
    );
    if (parentCategory && parentCategory.slug !== categoryFromUrl) {
      const params = new URLSearchParams(location.search);
      params.set("category", parentCategory.slug);
      navigate(
        `${location.pathname}?${params.toString()}`,
        { replace: true }
      );
    }
  }, [
    subCategoryFromUrl,
    categoryFromUrl,
    categories,
    location.pathname,
    location.search,
    navigate,
  ]);
  /**
   * PRICE RANGE
   */
  const { minPrice, maxPrice } = useMemo(() => {
    const mins = [];
    const maxs = [];
    data?.pages?.forEach((page) => {
      if (page?.filter_info?.price_min != null) {
        mins.push(Number(page.filter_info.price_min));
      }
      if (page?.filter_info?.price_max != null) {
        maxs.push(Number(page.filter_info.price_max));
      }
    });
    return {
      minPrice: mins.length ? Math.min(...mins) : 0,
      maxPrice: maxs.length ? Math.max(...maxs) : 10000,
    };
  }, [data]);
  /**
   * REMOVE SUBCATEGORY WHEN CATEGORY CHANGES
   */
  useEffect(() => {
    if (
      prevCategoryRef.current &&
      prevCategoryRef.current !== categoryFromUrl
    ) {
      const params = new URLSearchParams(location.search);
      params.delete("subcategory");
      navigate(
        `${location.pathname}?${params.toString()}`,
        { replace: true }
      );
    }
    prevCategoryRef.current = categoryFromUrl;
  }, [categoryFromUrl, location.pathname, location.search, navigate]);
  /**
   * REFRESH DATA ON URL CHANGE
   */
  // useEffect(() => {
  //   refetch();
  // }, [categoryFromUrl, subCategoryFromUrl, refetch]);
  const lastItemRef = useInfiniteScroll(
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  );  
  useEffect(() => {
  if ("scrollRestoration" in window.history) {
   
  }
  window.scrollTo({ top: 0, behavior: "auto" });
}, []);
  console.log("===== T-SHIRTS PRODUCT DEBUG =====");

console.log("Selected category:", categoryFromUrl);
console.log("Selected subcategory:", subCategoryFromUrl);
console.log("Selected child category:", childCategoryFromUrl);

console.log("Raw API data:", data);
console.log("API pages:", data?.pages);

console.log(
  "Products in first page:",
  data?.pages?.[0]?.products
);

console.log("Final products:", products);

console.log("==================================");
  return (
    <section className={styles.container}>
      <BreadcrumbBanner
        heading={"Explore stylish collections  and accessories all in one place"}
        imageUrl="https://images.pexels.com/photos/8398778/pexels-photo-8398778.jpeg?_gl=1*oajdm3*_ga*MTAzODIwMzc4Mi4xNzY5MTY5NjM0*_ga_8JE65Q40S6*czE3Nzk2ODUyMjUkbzUkZzEkdDE3Nzk2ODUzMDUkajU5JGwwJGgw"
extraCrumbs={[
  categoryFromUrl && {
    label: formatLabel(categoryFromUrl),
    link: `/shop?category=${categoryFromUrl}`,
  },
  subCategoryFromUrl && {
    label: formatLabel(subCategoryFromUrl),
    link: `/shop?category=${categoryFromUrl}&subcategory=${subCategoryFromUrl}`,
  },
  childCategoryFromUrl && {
    label: formatLabel(childCategoryFromUrl),
    link: `/shop?category=${categoryFromUrl}&subcategory=${subCategoryFromUrl}&childcategory=${childCategoryFromUrl}`,
  },
].filter(Boolean)}
      />
      <div className={styles.mainContent}>
        <div className={styles.contentWrapper}>
          <div className={styles.sidebarWrapper}>
            <ShopFilters
              minPrice={minPrice}
              maxPrice={maxPrice}
              categories={categories} 
               brands={brands} 
               colors={colors} 
              filtersProps={filtersProps}
              filtersActions={filtersActions}
            />
          </div>
          <div className={styles.rightContent}>
            <div className={styles.sortSection}>
              <button
                onClick={() => setMobileFiltersOpen(true)}
                className={styles.mobileFilterButton}
              >
                <IoFilterSharp /> Filters
              </button>
              <div className={styles.sortRight}>
                <SortFilter
                  heading="Sort By :"
                  sortBy={sortBy}
                  setSortBy={setSortBy}
                />
              </div>
            </div>  

            <ShopProducts     
              products={products}
              isPending={isPending}
              isError={isError}
              hasMore={hasNextPage}
              isFetching={isFetchingNextPage}
              lastItemRef={lastItemRef}
                wishItems={wishItems}
  isWishListPending={isWishListPending}
            /> 
            {(currentSubCategoryDescription || currentCategoryDescription) && (
  <div
    className={styles.categoryDescription}
    dangerouslySetInnerHTML={{
      __html: currentSubCategoryDescription || currentCategoryDescription,
    }}
  />
)}
          </div>
        </div>
      </div>
        {/* <ProductPopup products={products} />  */}
    </section>
  );
};
const BreadcrumbBanner = ({ heading, imageUrl, extraCrumbs }) => {
  const breadcrumbPaths = [
    { label: "Shop", path: "/shop" },
    ...extraCrumbs.map((c) => ({
      label: c.label,
      path: c.link,
    })),
  ];
  return (
    <>
      <BreadCrumb customPaths={breadcrumbPaths} />
      <Banner heading={heading} imageUrl={imageUrl} />
    </>
  );
}; 
export default ShopCategories;